import React, { useCallback, useEffect } from "react";
import { Button, Modal } from "react-bootstrap";
import { useLab } from "../../_helper/PharmacyProvider";
import { formatDoctorDisplayDateTime } from "../../utils/dateTime/dateFormat";
import { useLoader } from "../../_helper/LoaderContext";
import { getInvoiceDetail } from "./service/ledger-service";
import { useCurrency } from "../../_helper/currencyContext";
import { getLabTax } from "../../utils/taxConfig";
import logoDark1 from '../../../src/assets/images/logo/logo_dark_1.png';

const Payment = ({ show, onHide, trData }) => {

    const onPrint = useCallback(() => {
        const content = document.getElementById("invoice-content");
        if (!content) return;

        const printWin = window.open("", "", "width=800,height=auto,left=200,top=50,bottom=50");
        if (!printWin) return; // popup blocked

        const html = content.innerHTML;

        printWin.document.write(`
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Receipt</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        body {
            margin: 0 !important;
            padding: 0 !important;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 12px;
            line-height: 1.3;
            color: #000;
        }
        .receipt {
            width: 72mm;          /* ← most common printable width for 80mm rolls */
            /* width: 52mm; */    /* uncomment for 58mm printers */
            margin: 0 auto;
            padding: 4mm 2mm 6mm 2mm;  /* reduced top/bottom padding */
        }
        .center-block { text-align: center; }
        table { width: 100%; border-collapse: collapse; }
        td, th { padding: 2px 0; }
        .text-end { text-align: right; }
        .dotted { border-bottom: 1px dotted #000; margin: 4px 0; }
        .flex { display: flex; justify-content: space-between; align-items: center; }

        /* Critical thermal printer fixes */
        @page {
            size: 80mm auto !important;   /* or 72mm auto or 58mm auto */
            margin: 0mm !important;
            margin-bottom:4mm !important;
        }
        @media print {
            html, body {
                width: 80mm !important;
                height: auto !important;
                margin: 0 !important;
                padding: 0 !important;
            }
            .print-spacer { 
             margin-bottom: 4mm;
             text-align:center 
            }
            .receipt {
                margin: 0 !important;
                padding-top: 2mm !important;   /* ← almost no top space */
                padding-left: 8mm !important; 
            }
            /* Try to kill browser headers/footers */
            @page :first { margin: 0mm; }
        }
    </style>
</head>
<body >
    <div class="receipt">
        ${html}
        <div class="print-spacer">Thank you for your visit. Please come again!</div>
    </div>
    <script>
  window.onload = () => {
    window.print();
  };

  window.onafterprint = () => {
    // delay avoids Chrome race condition
    setTimeout(() => window.close(), 300);
  };
</script>
</body>
</html>
    `);

        printWin.document.close();
        printWin.focus();
    }, []);
    const { currentLab } = useLab();
    const labTax = getLabTax(currentLab);
    const taxLabel = labTax.taxType === "vat" ? "VAT" : "GST";
    const billDate = (() => {
        const formatted = formatDoctorDisplayDateTime(trData?.invoiceDate, currentLab);
        if (formatted) return formatted;
        const parsed = new Date(trData?.invoiceDate);
        if (Number.isNaN(parsed.getTime())) return "-";
        return formatDoctorDisplayDateTime(parsed, currentLab) || "-";
    })();
    const { setLoading } = useLoader();
    const { getCurrencySymbol } = useCurrency()
    const headerLogo = JSON.parse(localStorage.getItem("header"))
    return (
        <Modal show={show} onHide={onHide} scrollable centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    Transaction Detail
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="">
                <div id="invoice-content" style={{ fontSize: "14px" }}>
                    {/* Header */}
                    <div className="text-center center-block">
                        {/* {headerLogo.headerLogo !== "" && ( */}
                        <img src={`${process.env.REACT_APP_IMAGE}${headerLogo.headerLogo}` || logoDark1} style={{ width: '100px', objectFit: 'contain' }} alt="" onError={(e) => {
                            e.currentTarget.src = logoDark1;
                        }} />
                        {/* // )} */}
                        <div className="tw-text-lg tw-font-bold">{currentLab?.facility_name}</div>
                        {currentLab?.contact_person?.address !== "" && (
                            <>
                                <small>
                                    {currentLab?.contact_person?.address}
                                </small><br />
                                <small>
                                    {currentLab?.contact_person?.city}
                                </small><br />
                            </>
                        )}
                        <small>Mob: {currentLab?.contact_person?.phone_code} {currentLab?.contact_person?.mobile}</small>
                    </div>

                    <hr />
                    <div className="d-flex justify-content-between mb-2 flex">
                        <div>

                            <small>
                                Patient: <strong>{trData.patientInfo?.name}</strong>
                            </small><br />
                            <small>
                                Age: {trData?.patientInfo?.age} Years, Gender: {trData?.patientInfo?.gender}
                            </small>
                        </div>
                        <div className="text-end">
                            <small>Bill Date: {billDate}</small> <br />
                            {trData.doctorInfo?.name !== "" && (
                                <>
                                    <small>
                                        <div className="">
                                            Referred by: {trData?.doctorInfo?.name}
                                        </div>
                                    </small>

                                </>
                            )}
                            <small>
                                Payment Type: {trData?.paymentDetails?.paymentType}
                            </small>
                        </div>
                    </div>
                    <hr />

                    {/* Test Items */}
                    <table style={{ width: "100%", fontSize: "12px", lineHeight: "1.4" }}>
                        <thead className="tw-bg-gray-500 text-white">
                            <tr className="">
                                <th className="p-2" style={{ width: "80%", textAlign: "left" }}>Test Description</th>
                                {/* <th></th> */}
                                <th className="p-2" style={{ width: "20%", textAlign: "right", }}>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {trData?.testDetails?.map((item, index) => (
                                <tr className="border-bottom" key={index}>
                                    <td className="px-2 py-1" style={{ width: "80%", fontWeight: 600 }}>{item.testName}</td>
                                    <td className="px-2 py-1" style={{ width: "20%", textAlign: "right", fontWeight: 600 }}>{getCurrencySymbol(currentLab?.baseCurrency)}{item.cost}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Summary */}
                    <div className="row m-0">
                        <div className="col-md-6"></div>
                        <div className="col-md-6 px-2">
                            <table style={{ width: "100%", fontSize: "12px", marginTop: "8px", marginBottom: '10px' }}>
                                <tbody>
                                    {trData.paymentDetails?.currency !== currentLab?.baseCurrency && (
                                        <tr>
                                            <td><strong>Subtotal ({getCurrencySymbol(currentLab?.baseCurrency)})</strong></td>
                                            <td className="text-end">
                                                <strong>
                                                    {getCurrencySymbol(currentLab?.baseCurrency)}
                                                    {trData?.testDetails
                                                        ?.reduce((sum, item) => sum + parseFloat(item.cost || 0), 0)
                                                        ?.toFixed(2)}
                                                </strong>
                                            </td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td>Subtotal</td>
                                        <td className="text-end">{getCurrencySymbol(trData?.paymentDetails?.currency)}{trData?.paymentDetails?.totalPrice}</td>
                                    </tr>
                                    {trData?.paymentDetails?.discount?.percentage > 0 && (
                                        <tr>
                                            <td>Discount ({trData?.paymentDetails?.discount?.percentage}%)</td>
                                            <td className="text-end">-{getCurrencySymbol(trData?.paymentDetails?.currency)}{trData?.paymentDetails?.discount?.amount?.toFixed(2)}</td>
                                        </tr>
                                    )}
                                    {Number(trData?.paymentDetails?.IGSTPercentage) > 0 && (
                                        <tr>
                                            <td>{taxLabel} ({trData?.paymentDetails?.IGSTPercentage}%)</td>
                                            <td className="text-end">+{getCurrencySymbol(trData?.paymentDetails?.currency)}{Number(trData?.paymentDetails?.IGST || 0).toFixed(2)}</td>
                                        </tr>
                                    )}
                                    <tr className="fw-bold">
                                        <td>Grand Total</td>
                                        <td className="text-end">{getCurrencySymbol(trData?.paymentDetails?.currency)}{trData?.paymentDetails?.netTotal?.toFixed(2)}</td>
                                    </tr>
                                    <tr className="fw-bold">
                                        <td>Paid Amount</td>
                                        <td className="text-end">{getCurrencySymbol(trData?.paymentDetails?.currency)}{trData?.paymentDetails?.paid?.toFixed(2)}</td>
                                    </tr>
                                    {trData?.paymentDetails?.due > 0 && (
                                        <tr className="tw-bg-neutral-300">
                                            <td>Balance Due</td>
                                            <td className="text-end">{getCurrencySymbol(trData?.paymentDetails?.currency)}{trData?.paymentDetails?.due.toFixed(2)} </td>
                                        </tr>
                                    )}
                                    <tr></tr>
                                </tbody>
                            </table>

                        </div>
                    </div>

                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="primary" onClick={onPrint}>
                    Print Receipt
                </Button>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default Payment;